import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api, ApiError, Catalog, Me } from './api';

interface SessionState {
  me: Me | null;
  catalog: Catalog | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([api.get<Me>('/me'), api.get<Catalog>('/catalog')]);
      setMe(m);
      setCatalog(c);
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) throw e;
      setMe(null);
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout', {});
    setMe(null);
    setCatalog(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <Ctx.Provider value={{ me, catalog, loading, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const s = useContext(Ctx);
  if (!s) throw new Error('SessionProvider отсутствует');
  return s;
}

/** Подразделение и ООО по умолчанию из ролей: руководителю отдела — его отдел. */
export function defaults(me: Me | null) {
  const head = me?.roles.find((r) => r.role === 'dept_head');
  const withLe = me?.roles.find((r) => r.legal_entity_id);
  return { departmentId: head?.department_id ?? null, legalEntityId: withLe?.legal_entity_id ?? null };
}

export function hasRole(me: Me | null, ...roles: string[]) {
  return !!me?.roles.some((r) => roles.includes(r.role));
}
